import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AccountRequestDTO, AccountRequestHistoryDTO, AuthDetails, FormDTO, AddressDTO, OwnerDetailsDTO } from '../../../types';
import { useAuth } from '../../../context/AuthContext';
import { formatTimestampTime } from '../../../formatTimestamp';
import {mdiClose, mdiContentCopy, mdiCheck, mdiLockOutline, mdiDownload} from '@mdi/js';
import { Icon } from '@mdi/react';


import './AccountRequestDetails.scss';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';
import DocuSignInfo from '../../DocuSign/DocuSignInfo';

// Updated interfaces for the new structure
interface FieldDTO {
  label: string;
  value: string;
}

interface SectionDTO {
  title: string;
  alias: string;
  fields: FieldDTO[];
}

interface SignerDTO {
  name: string;
  role: string;
  email: string;
}

interface InternalFormDTO {
  sections: SectionDTO[];
  signers: SignerDTO[];
}


interface AccountRequestDetailDTO extends AccountRequestDTO {
  // Additional fields
}

// CopyableField component for click-to-copy functionality
const CopyableField: React.FC<{ value: string | null | undefined; children?: React.ReactNode }> = ({ value, children }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  if (!value && !children) return <span>-</span>;
  
  return (
    <span className="copyable-field" onClick={handleCopy}>
      <span className="copyable-content">
        {children || value}
      </span>
      <span className="copy-icon">
        <span className={`icon-copy ${copied ? 'fade-out' : 'fade-in'}`}>
          <Icon path={mdiContentCopy} size={0.5} />
        </span>
        <span className={`icon-check ${copied ? 'fade-in' : 'fade-out'}`}>
          <Icon path={mdiCheck} size={0.5} />
        </span>
      </span>
    </span>
  );
};

// AddressDisplay component for rendering address information
const AddressDisplay: React.FC<{ address: AddressDTO }> = ({ address }) => {
  const formatAddress = (addr: AddressDTO) => {
    const parts = [];
    if (addr.street) parts.push(addr.street);
    if (addr.city) parts.push(addr.city);
    if (addr.region) parts.push(addr.region);
    if (addr.postalCode) parts.push(addr.postalCode);
    if (addr.country) parts.push(addr.country);
    return parts.join(', ');
  };

  return (
    <div className="address-display">
      <CopyableField value={formatAddress(address)}>
        <div>
          {address.street && <div>{address.street}</div>}
          <div>
            {address.city && <span>{address.city}</span>}
            {address.city && address.region && <span>, </span>}
            {address.region && <span>{address.region}</span>}
            {(address.city || address.region) && address.postalCode && <span> </span>}
            {address.postalCode && <span>{address.postalCode}</span>}
          </div>
          {address.country && <div>{address.country}{address.isPrimary && <small className="primary-indicator"> (Primary)</small>}</div>}
        </div>
      </CopyableField>
    </div>
  );
};

// Function to find the primary owner using account holder information
const findPrimaryOwner = (auth: AuthDetails): { primaryIndex: number; confidence: 'high' | 'medium' | 'low' } => {
  if (!auth.owners?.length) return { primaryIndex: -1, confidence: 'low' };
  
  for (let i = 0; i < auth.owners.length; i++) {
    const owner = auth.owners[i];
    let matches = 0;
    
    // Name matching (highest weight)
    if (auth.accountHolderName && owner.names?.length) {
      const accountName = auth.accountHolderName.toLowerCase().trim();
      if (owner.primaryName?.toLowerCase().trim() === accountName) {
        matches += 3;
      } else if (owner.names.some(name => name.toLowerCase().trim() === accountName)) {
        matches += 3;
      }
    }
    
    // Email matching
    if (auth.primaryEmail && owner.emails?.length) {
      const accountEmail = auth.primaryEmail.toLowerCase().trim();
      if (owner.primaryEmail?.toLowerCase().trim() === accountEmail) {
        matches += 2;
      } else if (owner.emails.some(email => email.toLowerCase().trim() === accountEmail)) {
        matches += 2;
      }
    }
    
    // Phone matching (normalize phone numbers by removing non-digits)
    if (auth.primaryPhone && owner.phoneNumbers?.length) {
      const normalizePhone = (phone: string) => phone.replace(/\D/g, '');
      const accountPhone = normalizePhone(auth.primaryPhone);
      if (owner.primaryPhone && normalizePhone(owner.primaryPhone) === accountPhone) {
        matches += 2;
      } else if (owner.phoneNumbers.some(phone => normalizePhone(phone) === accountPhone)) {
        matches += 2;
      }
    }
    
    // Address matching (basic comparison)
    if (auth.primaryAddress && owner.primaryAddress) {
      const authAddr = auth.primaryAddress;
      const ownerAddr = owner.primaryAddress;
      if (authAddr.street === ownerAddr.street && 
          authAddr.city === ownerAddr.city && 
          authAddr.postalCode === ownerAddr.postalCode) {
        matches += 1;
      }
    } else if (auth.primaryAddress && owner.addresses?.length) {
      const authAddr = auth.primaryAddress;
      if (owner.addresses.some(addr => 
          addr.street === authAddr.street && 
          addr.city === authAddr.city && 
          addr.postalCode === authAddr.postalCode)) {
        matches += 1;
      }
    }
    
    // Return early on strong match
    if (matches >= 4) return { primaryIndex: i, confidence: 'high' };
    if (matches >= 2) return { primaryIndex: i, confidence: 'medium' };
  }
  
  // Fallback to first owner if no strong matches
  return { primaryIndex: 0, confidence: 'low' };
};

// OwnerDisplay component for rendering owner information
const OwnerDisplay: React.FC<{ owner: OwnerDetailsDTO; index: number; isPrimary: boolean; ownerNumber: number }> = ({ owner, index, isPrimary, ownerNumber }) => {
  const getOwnerLabel = () => {
    if (isPrimary) {
      return "Primary Owner";
    }
    return `Owner ${ownerNumber}`;
  };

  return (
    <div className="form-section">
      <h4>{getOwnerLabel()}</h4>
      <dl>
        {/* Names */}
        {owner.names && owner.names.length > 0 && (
          <div className="item">
            <dt>Names</dt>
            <dd>
              {owner.names.length === 1 ? (
                <CopyableField value={owner.names[0]}>
                  {owner.names[0]}
                  {owner.primaryName === owner.names[0] && <small className="primary-indicator"> (Primary)</small>}
                </CopyableField>
              ) : (
                <ul className="owner-list">
                  {owner.names.map((name, i) => (
                    <li key={i}>
                      <CopyableField value={name}>
                        {name}
                        {owner.primaryName === name && <small className="primary-indicator"> (Primary)</small>}
                      </CopyableField>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        )}
        
        {/* Addresses */}
        {owner.addresses && owner.addresses.length > 0 && (
          <div className="item">
            <dt>Addresses</dt>
            <dd>
              {owner.addresses.length === 1 ? (
                <AddressDisplay address={owner.addresses[0]} />
              ) : (
                <ul className="owner-list">
                  {owner.addresses.map((address, i) => (
                    <li key={i}>
                      <AddressDisplay address={address} />
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        )}
        
        {/* Emails */}
        {owner.emails && owner.emails.length > 0 && (
          <div className="item">
            <dt>Emails</dt>
            <dd>
              {owner.emails.length === 1 ? (
                <CopyableField value={owner.emails[0]}>
                  {owner.emails[0]}
                  {owner.primaryEmail === owner.emails[0] && <small className="primary-indicator"> (Primary)</small>}
                </CopyableField>
              ) : (
                <ul className="owner-list">
                  {owner.emails.map((email, i) => (
                    <li key={i}>
                      <CopyableField value={email}>
                        {email}
                        {owner.primaryEmail === email && <small className="primary-indicator"> (Primary)</small>}
                      </CopyableField>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        )}
        
        {/* Phone Numbers */}
        {owner.phoneNumbers && owner.phoneNumbers.length > 0 && (
          <div className="item">
            <dt>Phone Numbers</dt>
            <dd>
              {owner.phoneNumbers.length === 1 ? (
                <CopyableField value={owner.phoneNumbers[0]}>
                  {owner.phoneNumbers[0]}
                  {owner.primaryPhone === owner.phoneNumbers[0] && <small className="primary-indicator"> (Primary)</small>}
                </CopyableField>
              ) : (
                <ul className="owner-list">
                  {owner.phoneNumbers.map((phone, i) => (
                    <li key={i}>
                      <CopyableField value={phone}>
                        {phone}
                        {owner.primaryPhone === phone && <small className="primary-indicator"> (Primary)</small>}
                      </CopyableField>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
};

const AccountRequestDetails: React.FC = () => {
    const { accessToken } = useAuth();
  const { id } = useParams();
  const [details, setDetails] = useState<AccountRequestDetailDTO | null>(null);
  const [history, setHistory] = useState<AccountRequestHistoryDTO[]>([]);
  const [form, setForm] = useState<InternalFormDTO | null>(null);
  const [auth, setAuth] = useState<AuthDetails | null>(null);
  const [getAuth, setGetAuth] = useState<boolean>(false);
  const navigate = useNavigate();
  
  // Tab management with URL params
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  
  const setActiveTab = (tab: string) => {
    setSearchParams({ tab });
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setDetails(null);
        setHistory([]);
        setAuth(null);
        setForm(null);
        setGetAuth(false);
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_BASEPATH}/account-requests/${id}`, {headers: {Authorization: `Bearer ${accessToken}`}});
        setDetails(response.data);
      } catch (error) {
        console.error('Error fetching account request details:', error);
        // Error will be handled by axios interceptor
      }
    };

    if (id) fetchDetails();
  }, [id]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_BASEPATH}/account-request-history/${details?.id}`, {headers: {Authorization: `Bearer ${accessToken}`}});
        setHistory(response.data);
      } catch (error) {
        console.error('Error fetching account request history:', error);
        // Error will be handled by axios interceptor
      }
    };

    if (details?.id) fetchHistory();
  }, [details, auth]);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setForm(null); // Clear previous form data
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_BASEPATH}/forms/${details?.externalId}`, {headers: {Authorization: `Bearer ${accessToken}`}});
        setForm(response.data);
      } catch (error) {
        console.error('Error fetching form data:', error);
        setForm(null); // Clear form data on error too
        // Error will be handled by axios interceptor
      }
    };

    if (details?.externalId) {
      fetchForm();
    } else {
      setForm(null); // Clear form data if no externalId
    }
  }, [details, auth]);
  
  useEffect(() => {
    const fetchAuth = async () => {
      try {
        setAuth(null); // Clear previous auth data
        const response = await axios.get(`${process.env.REACT_APP_SERVICE_BASEPATH}/account-requests/${id}/auth`, {headers: {Authorization: `Bearer ${accessToken}`}});
        setAuth(response.data);
      } catch (error) {
        console.error('Error fetching auth details:', error);
        setAuth(null); // Clear auth data on error too
        // Error will be handled by axios interceptor
      }
    };

    if (getAuth) fetchAuth();
  }, [getAuth]);

  const handleDownloadCsv = async () => {
    if (!id) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVICE_BASEPATH}/account-requests/${id}/csv`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `account_request_${id}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      // Error will be handled by axios interceptor
    }
  };

  const handleDownloadAccountInfo = async () => {
    if (!id) return;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVICE_BASEPATH}/account-requests/${id}/enhanced-csv`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `account_info_${id}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading account info:', error);
      // Error will be handled by axios interceptor
    }
  };

  const getAppStatusClass = (status: string | null | undefined) => {
    if (!status) return 'pending';
    
    switch (status.toLowerCase()) {
      case 'complete':
        return 'completed';
      case 'pending':
        return 'pending';
      case 'declined':
        return 'declined';
      case 'voided':
        return 'voided';
      default:
        return 'pending';
    }
  };

  // Component to render individual sections
  const FormSection: React.FC<{ section: SectionDTO }> = ({ section }) => (
    <div className="form-section">
      <h4>{section.title}</h4>
      <dl>
        {section.fields.map((field, index) => (
          <div className="item" key={`${section.alias}-${index}`}>
            <dt>{field.label}</dt>
            <dd><CopyableField value={field.value} /></dd>
          </div>
        ))}
      </dl>
    </div>
  );

  // Component to render signers
  const SignersSection: React.FC<{ signers: SignerDTO[] }> = ({ signers }) => {
    if (!signers || signers.length === 0) return null;
    
    return (
      <div className="form-section">
        <h4>Signers</h4>
        <dl>
          {signers.map((signer, index) => (
            <div className="item" key={`signer-${index}`}>
              <dt>{signer.role}</dt>
              <dd>
                <CopyableField value={`${signer.name}${signer.email ? ` (${signer.email})` : ''}`}>
                  <div className="signer-info">
                    <span className="signer-name">{signer.name}</span>
                    {signer.email && <span className="signer-email">({signer.email})</span>}
                  </div>
                </CopyableField>
              </dd>
            </div>
          ))}
        </dl>
      </div>
    );
  };

  // Tab content rendering functions
  const renderOverviewTab = () => {
    if (!details) return null;
    
    return (
      <>
        <div>
          <h3>Auth Details</h3>
          {!auth && details.accessTokenPresent && <div className='detail-section align-center'>
            {!getAuth && <button className='icon-button icon' onClick={() => setGetAuth(true)}>Show Auth Details <Icon path={mdiLockOutline} size={1} /></button>}
            {getAuth && <LoadingIndicator />}
          </div>}
          {auth && 
            <div>
              {/* Account Details */}
              <div className="form-section">
                <h4>Account Details</h4>
                <dl>
                  {auth.accountHolderName && (
                    <div className="item">
                      <dt>Account Name</dt>
                      <dd><CopyableField value={auth.accountHolderName} /></dd>
                    </div>
                  )}
                  {auth.accountType && (
                    <div className="item">
                      <dt>Account Type</dt>
                      <dd><CopyableField value={auth.accountSubtype} /></dd>
                    </div>
                  )}
                  {auth.account && (
                    <div className="item">
                      <dt>Account Number</dt>
                      <dd><CopyableField value={auth.account} /></dd>
                    </div>
                  )}
                  {auth.routing && (
                    <div className="item">
                      <dt>Routing Number</dt>
                      <dd><CopyableField value={auth.routing} /></dd>
                    </div>
                  )}
                  {auth.instId && (
                    <div className="item">
                      <dt>Bank Name</dt>
                      <dd><CopyableField value={auth.instId} /></dd>
                    </div>
                  )}
                  {auth.taxId && (
                    <div className="item">
                      <dt>Tax ID</dt>
                      <dd><CopyableField value={auth.taxId} /></dd>
                    </div>
                  )}
                </dl>
              </div>
              
              
              {/* Individual Owner Cards */}
              {auth.owners && auth.owners.length > 0 && (() => {
                const { primaryIndex } = findPrimaryOwner(auth);
                let nonPrimaryCount = 1; // Counter for non-primary owners
                return auth.owners.map((owner, index) => {
                  const isPrimary = index === primaryIndex;
                  const ownerNumber = isPrimary ? 1 : ++nonPrimaryCount;
                  
                  return (
                    <OwnerDisplay 
                      key={index} 
                      owner={owner} 
                      index={index} 
                      isPrimary={isPrimary}
                      ownerNumber={ownerNumber}
                    />
                  );
                });
              })()}
            </div>
          }
          {!details.accessTokenPresent && 
            <dl>
              <div className="item">
                <dt>Awaiting auth</dt>
                <dd></dd>
              </div>
            </dl>
          }
        </div>

        <h3>Request Info</h3>
        <dl>
          {/* <div className='item'>
            <dt>Request Link</dt>
            <dd><CopyableField value={`${process.env.REACT_APP_INVEST_APP_BASEPATH}/request/${details.token}`}>
              <a href={`${process.env.REACT_APP_INVEST_APP_BASEPATH}/request/${details.token}`} target='_blank'>{process.env.REACT_APP_INVEST_APP_BASEPATH}/request/{details.token}</a>
            </CopyableField></dd>
          </div> */}
          <div className="item">
            <dt>Requested By</dt>
            <dd><CopyableField value={details.requestedBy} /></dd>
          </div>
          <div className="item">
            <dt>Type</dt>
            <dd><CopyableField value={details.type} /></dd>
          </div>
          <div className="item">
            <dt>To Email</dt>
            <dd><CopyableField value={details.toEmail} /></dd>
          </div>
          <div className="item">
            <dt>To Phone</dt>
            <dd><CopyableField value={details.toPhone} /></dd>
          </div>
          <div className="item">
            <dt>Token</dt>
            <dd><CopyableField value={details.token} /></dd>
          </div>
          <div className="item">
            <dt>Date Created</dt>
            <dd><CopyableField value={formatTimestampTime(details.dateCreated.toString())} /></dd>
          </div>
          <div className="item">
            <dt>Status</dt>
            <dd><CopyableField value={details.status} /></dd>
          </div>
          <div className="item">
            <dt>Note</dt>
            <dd><CopyableField value={details.note} /></dd>
          </div>
          <div className="item">
            <dt>Account Number</dt>
            <dd><CopyableField value={details.accountNumber} /></dd>
          </div>
          <div className="item">
            <dt>First Name</dt>
            <dd><CopyableField value={details.firstName} /></dd>
          </div>
          <div className="item">
            <dt>Last Name</dt>
            <dd><CopyableField value={details.lastName} /></dd>
          </div>
          <div className="item">
            <dt>Acct Type</dt>
            <dd><CopyableField value={details.accountType === 'business' ? 'Business' : 'Individual'} /></dd>
          </div>
          {details.businessName && (
            <div className="item">
              <dt>Business Name</dt>
              <dd><CopyableField value={details.businessName} /></dd>
            </div>
          )}
          {details.referralSource && (
            <div className="item">
              <dt>Referral Source</dt>
              <dd><CopyableField value={details.referralSource} /></dd>
            </div>
          )}
        </dl>

        <h3>Export Options</h3>
        <div className='detail-section align-center' style={{display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center'}}>
          <button 
            className='icon-button icon' 
            onClick={handleDownloadAccountInfo}
            title="Download bank account information (same format as batch export)"
          >
            <Icon path={mdiDownload} size={1} />
            Bank Information
          </button>
          <button 
            className='icon-button icon' 
            onClick={handleDownloadCsv}
            title="Download detailed request data with form fields as CSV"
          >
            <Icon path={mdiDownload} size={1} />
            All Information
          </button>
        </div>
      </>
    );
  };

  const renderSubmissionTab = () => (
    <>
      {form && (
        <>
          <h3>Submission Data</h3>
          <div className="submission-data">
            {/* Render all sections */}
            {form.sections?.map((section, index) => (
              <FormSection key={`section-${index}`} section={section} />
            ))}
            
            {/* Render signers if present */}
            <SignersSection signers={form.signers} />
          </div>
        </>
      )}
      {!form && (
        <div className='detail-section align-center'>
          <p>No submission data available</p>
        </div>
      )}
    </>
  );

  const renderDocuSignTab = () => {
    if (!details) return null;
    
    return (
      <>
        {details.externalId ? (
          <DocuSignInfo formId={details.externalId} />
        ) : (
          <div className='detail-section align-center'>
            <p>No DocuSign information available</p>
          </div>
        )}
      </>
    );
  };

  const renderHistoryTab = () => (
    <>
      <h3>History</h3>
      <div className='history detail-section'>
        <ul>
          {history.map((history) => (
            <li key={`history-${history.timestamp}`}>
              <div className='history-list-item'>
                <span className='date'><CopyableField value={formatTimestampTime(history.timestamp.toString())} /></span>
                <span className='event'><CopyableField value={history.event} /></span>
                <div className='detail'><CopyableField value={history.detail} /></div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  if (!details) return <div className='account-request-loading'>
    <LoadingIndicator />
  </div>;


  
  

  return (
    <div className='account-request-details fade-in'>
      <div className='details-header'>
        <h2>REQ #{details.id}</h2>
        <span className='date-created'>Created {formatTimestampTime(details.dateCreated.toString())}</span><br />
        <span className={'tag-' + details.status }>{details.status}</span>
        <button className='close-button' onClick={() => navigate('/account-requests')}><Icon path={mdiClose} size={1} /></button>
      </div>
      
      {/* Tab Navigation */}
      <div className='tab-navigation'>
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'submission' ? 'active' : ''}`}
          onClick={() => setActiveTab('submission')}
        >
          Form
        </button>
        <button 
          className={`tab-button ${activeTab === 'docusign' ? 'active' : ''}`}
          onClick={() => setActiveTab('docusign')}
        >
          Signing
        </button>
        <button 
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>
      
      <div className='details-body'>
        {/* Render content based on active tab */}
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'submission' && renderSubmissionTab()}
        {activeTab === 'docusign' && renderDocuSignTab()}
        {activeTab === 'history' && renderHistoryTab()}
      </div>
    </div>
  );
};

export default AccountRequestDetails;
